<script setup lang="ts">
const { currentPalette } = useTheme();
const { nl2br, striptags } = useHelper();

const props = defineProps<{
  post?: PostItem | null;
}>();

const enabled = computed(() => props.post?.cta_enabled || false);

const titleNormal = computed(() => props.post?.cta_title_normal || "");
const titleDecorative = computed(() => props.post?.cta_title_decorative || "");

const content = computed(() => props.post?.cta_content || "");

const actions = computed(() => props.post?.cta_actions || []);

const getActionIcon = useMemoize((type: string): string => {
  return (
    {
      phone: "ns:phone",
      email: "ns:mail",
      location: "ns:map-pin",
    }[type] || ""
  );
});

const getActionLink = useMemoize((type: string, value: string): string => {
  value = encodeURIComponent(value.trim());

  return (
    {
      phone: `tel:${value}`,
      email: `mailto:${value}`,
      location: `https://www.google.com/maps/search/?api=1&query=${value}`,
    }[type] || ""
  );
});
</script>

<template>
  <section v-if="enabled" class="post-cta">
    <div class="post-cta__container lg:container">
      <div
        class="post-cta__wrapper px-5 py-8 lg:p-10"
        :class="[currentPalette.bg]"
      >
        <div
          v-if="titleNormal || titleDecorative"
          class="post-cta__title text-white contrast-fix"
        >
          <h2
            class="post-cta_title text-[1.75rem] leading-[2.5rem] lg:text-[3.125rem] lg:leading-[2.75rem]"
          >
            <span v-if="titleNormal" class="inline">
              {{ titleNormal }}
            </span>
            <span
              v-if="titleNormal && titleDecorative"
              class="inline tracking-[0.25rem]"
              >&emsp14;</span
            >
            <span
              v-if="titleDecorative"
              class="inline font-display tracking-[0.3125rem]"
            >
              {{ titleDecorative }}
            </span>
          </h2>
        </div>

        <div v-if="content" class="post-cta__content mt-6">
          <p
            v-html="nl2br(striptags(content))"
            class="text-sm leading-6 font-medium lg:font-semibold"
          ></p>
        </div>

        <div
          v-if="actions && actions.length > 0"
          class="post-cta__actions mt-6 lg:mt-10"
        >
          <ul
            class="post-cta__actions-wrapper flex flex-col lg:flex-row flex-wrap gap-2 lg:gap-x-24"
          >
            <li v-for="item in actions" class="post-cta__item">
              <a
                :href="getActionLink(item.type, item.value)"
                target="_blank"
                rel="noopener nofollow"
                class="flex items-center justify-start gap-6 lg:gap-2 min-h-11 select-none"
              >
                <Icon
                  v-if="getActionIcon(item.type)"
                  :name="getActionIcon(item.type)"
                  class="min-w-6 lg:min-w-8 basis-6 lg:basis-8 w-6 h-6 lg:w-8 lg:h-8"
                />
                <span class="text-sm leading-7 lg:text-lg lg:leading-[2.75rem]">
                  {{ item.value }}
                </span>
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </section>
</template>
